/** Stable parent integration keys, separate from plan entitlements and child capabilities. */
export const GOVERNMENT_INTEGRATION_MODULE_PATHS = {
  services_australia: '/settings/integrations/services-australia',
  hi: '/settings/integrations/hi',
  dhda_b2g: '/settings/integrations/dhda',
} as const;
export type GovernmentIntegration = keyof typeof GOVERNMENT_INTEGRATION_MODULE_PATHS;
export type GovernmentIntegrationEnablement = Record<GovernmentIntegration, boolean>;
export const DISABLED_GOVERNMENT_INTEGRATIONS: GovernmentIntegrationEnablement = {
  services_australia: false,
  hi: false,
  dhda_b2g: false,
};

/** A legacy null module list never implies government integration access. */
export function projectGovernmentIntegrationModules(
  modules: string[] | null,
  enabled: GovernmentIntegrationEnablement,
): string[] {
  const integrationPaths: readonly string[] = Object.values(GOVERNMENT_INTEGRATION_MODULE_PATHS);
  return [
    ...(modules ?? []).filter((path) => !integrationPaths.includes(path)),
    ...Object.entries(GOVERNMENT_INTEGRATION_MODULE_PATHS)
      .filter(([key]) => enabled[key as GovernmentIntegration])
      .map(([, path]) => path),
  ];
}

/** Government-only routes. Mixed local workspaces deliberately have no parent requirement. */
export function governmentIntegrationForPath(path: string): GovernmentIntegration | null {
  const [segment] = path.split(/[?#]/, 1);
  if (segment === undefined) return null;
  const pathname = segment.replace(/\/$/, '');
  const paths: Record<GovernmentIntegration, readonly string[]> = {
    services_australia: [
      '/settings/integrations/services-australia',
      '/integrations/services-australia',
      '/compliance/services-australia',
      '/home-care/claims/services-australia',
      '/api/integrations/services-australia',
    ],
    hi: ['/settings/integrations/hi', '/clients/batch-validation'],
    dhda_b2g: [
      '/settings/integrations/dhda',
      '/compliance/b2g',
      '/compliance/rn-coverage/reporting',
      '/compliance/quality/qi-data-entry/reporting',
    ],
  };
  return (
    (Object.keys(paths) as GovernmentIntegration[]).find((key) =>
      paths[key].some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)),
    ) ?? null
  );
}

export function isGovernmentIntegrationPathVisible(
  path: string,
  enabled?: GovernmentIntegrationEnablement | null,
  role?: string | null,
): boolean {
  const [segment] = path.split(/[?#]/, 1);
  if (segment === undefined) return false;
  if (segment.replace(/\/$/, '') === '/settings/integrations') {
    return role === 'OWNER' || role === 'TENANT_ADMIN';
  }
  const integration = governmentIntegrationForPath(path);
  return integration === null || enabled?.[integration] === true;
}
